import React, { useState } from "react";
import { useActor } from "@xstate/react";
import TextInput, { isValidTextInput } from "../TextInput";
import { Button } from "../SignUpButton";
import { Loader } from "@pushpress/shared-components";
import { useUpdatePasswordMutation } from "../../../../apis/loginApi.hooks";
import { toast } from "react-toastify";
import { isLocal } from "../../../../env";
import { useSignUpMachine } from "../../context/SignUpMachineContext";
import { SignUpFormField } from "../../constants/signup-form-field.enum";
import { TextInputType } from "../../constants/signup.types";

const CreatePasswordStep: React.FC = () => {
  const machine = useSignUpMachine();
  const [state, send] = useActor(machine);
  const { formData, subdomain } = state.context;

  const [isLoading, setIsLoading] = useState(false);
  const updatePassword = useUpdatePasswordMutation();

  const passwordValue = formData[SignUpFormField.Password];
  const emailValue = formData[SignUpFormField.Email];

  const field = {
    id: SignUpFormField.Password,
    inputType: "password",
    label: "Password",
    required: true,
  };

  const handleChange = (value: any) => {
    send({
      type: "UPDATE_FORM",
      data: { [SignUpFormField.Password]: value },
    });
  };

  const handleSubmit = async () => {
    if (!emailValue || !passwordValue) {
      toast.error("Missing information to set password.");
      return;
    }
    setIsLoading(true);

    try {
      const result = await updatePassword.mutateAsync({
        email: emailValue,
        password: passwordValue,
      });

      if (!result) {
        toast.error("Error creating password. Please try again.");
        setIsLoading(false);
        return;
      }

      const protocol = isLocal() ? "http" : "https";
      const finalSubdomain = subdomain || "app";
      const domainBase = process.env.BASE_URL || "pushpress.com";
      window.location.href = `${protocol}://${finalSubdomain}.${domainBase}/dashboard/implicit_grant?free=true`;
    } catch (error) {
      console.error("Set password failed:", error);
      toast.error("Error setting password. Please try again.");
      setIsLoading(false);
    }
  };

  const canProceed = isValidTextInput(
    passwordValue,
    field.inputType as TextInputType,
  );

  return (
    <>
      <div className="tw-flex tw-flex-col tw-items-center tw-space-y-1 tw-text-center">
        <h2 className="tw-text-xl">Create a password</h2>
        <p className="tw-text-gray-400 tw-text-base">
          Create a password to access your account
        </p>
      </div>
      <div className="tw-space-y-4">
        <TextInput
          key={field.id}
          id={field.id}
          value={passwordValue}
          onChange={handleChange}
          inputType={field.inputType as TextInputType}
          label={field.label}
          required={field.required}
        />
      </div>
      <div className="tw-flex tw-justify-between tw-items-center">
        <Button
          onClick={handleSubmit}
          disabled={!canProceed || isLoading}
          size="fat"
          className="tw-uppercase tw-tracking-widest tw-w-full tw-flex tw-py-4 tw-items-center tw-gap-2 tw-text-white"
        >
          {isLoading ? <Loader color="white" /> : <span>Go to Dashboard</span>}
        </Button>
      </div>
    </>
  );
};

export default CreatePasswordStep;
